const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8001';

class WebSocketService {
  constructor() {
    this.connections = new Map();
    this.reconnectAttempts = new Map();
    this.reconnectTimers = new Map(); // channel -> setTimeout handle, so disconnect() can cancel a scheduled reconnect
    this.intentionalClose = new Set(); // channels closed on purpose — no reconnect
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 5000;
  }

  connect(channel, onMessage, onError = null) {
    if (this.connections.has(channel)) {
      console.log(`Already connected to channel: ${channel}`);
      return;
    }

    // Clear any prior intentional-close flag so reconnects work after re-mount
    this.intentionalClose.delete(channel);

    const ws = new WebSocket(`${WS_URL}/ws/${channel}`);

    ws.onopen = () => {
      console.log(`Connected to channel: ${channel}`);
      this.reconnectAttempts.set(channel, 0);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error(`WebSocket error on channel ${channel}:`, error);
      if (onError) {
        onError(error);
      }
    };

    ws.onclose = (event) => {
      console.log(`Disconnected from channel: ${channel} (code=${event.code})`);
      this.connections.delete(channel);

      // Do NOT reconnect if this was an intentional disconnect
      if (this.intentionalClose.has(channel)) {
        this.intentionalClose.delete(channel);
        return;
      }

      // 1008 = policy violation (auth/403), 4003 = custom forbidden
      // 1006 = abnormal closure (connection refused / bad URL) — cap retries at 2
      if (event.code === 1008 || event.code === 4003) {
        console.error(`WebSocket auth rejected on channel ${channel} (code=${event.code}) — not retrying`);
        this.reconnectAttempts.delete(channel);
        return;
      }

      // Attempt to reconnect
      const attempts = this.reconnectAttempts.get(channel) || 0;
      if (attempts < this.maxReconnectAttempts) {
        console.log(`Reconnecting to ${channel} (attempt ${attempts + 1}/${this.maxReconnectAttempts})...`);
        const timer = setTimeout(() => {
          this.reconnectTimers.delete(channel);
          this.reconnectAttempts.set(channel, attempts + 1);
          this.connect(channel, onMessage, onError);
        }, this.reconnectDelay);
        this.reconnectTimers.set(channel, timer);
      } else {
        console.error(`Max reconnection attempts reached for channel: ${channel}`);
        this.reconnectAttempts.delete(channel);
      }
    };

    this.connections.set(channel, ws);
  }

  disconnect(channel) {
    // A reconnect may already be scheduled (a prior attempt failed and is waiting out
    // reconnectDelay) even when there's no live `ws` for this channel right now — without
    // this, that timer fires after the page navigated away and opens a brand-new
    // connection nobody is listening to anymore.
    const timer = this.reconnectTimers.get(channel);
    if (timer) {
      clearTimeout(timer);
      this.reconnectTimers.delete(channel);
    }
    const ws = this.connections.get(channel);
    if (ws) {
      // Mark as intentional before calling close() so onclose skips reconnect
      this.intentionalClose.add(channel);
      this.reconnectAttempts.delete(channel);
      ws.close();
      this.connections.delete(channel);
      console.log(`Manually disconnected from channel: ${channel}`);
    }
  }

  send(channel, data) {
    const ws = this.connections.get(channel);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    } else {
      console.error(`Cannot send message: not connected to channel ${channel}`);
    }
  }

  disconnectAll() {
    // Iterate reconnectTimers too — a channel can have a pending reconnect with no live
    // `ws` in `connections` at all (its previous socket already closed), and disconnect()
    // only clears what's passed to it, so looping over connections alone would miss it.
    const channels = new Set([...this.connections.keys(), ...this.reconnectTimers.keys()]);
    channels.forEach((channel) => this.disconnect(channel));
  }
}

export default new WebSocketService();
